import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faDownload,
  faPenToSquare,
  faSignal,
  faSpinner,
  faTrash,
  faXmark,
  IconDefinition,
} from "@fortawesome/free-solid-svg-icons";

import { SortOrder } from "../../service/utils/constants";
import DropdownSelect from "../dropdown-select/DropdownSelect";
import SearchInputRow from "../search-input-box/SearchInputBox";
import { SortSettings } from "./SortableTable";
import "./SortableTable.scss";
import TableLabels from "./table-labels/TableLabels";

interface WeightronAction {
  action?: (row: any[]) => void;
  icon: IconDefinition;
}

interface SortableTableUIProps {
  standaloneTable?: boolean;
  currentFilterKey?: string;
  header: string[];
  id: string;
  currentSort?: SortSettings;
  pageRows: any[][];
  isAuditTable?: boolean;
  isDocumentTable?: boolean;
  isWeightingTable?: boolean;
  isTippingUser?: boolean;
  hasDownloadLinks?: boolean;
  isTestingConnection?: any;
  currentPage: number;
  totalPages: number;

  setMaxRowsPerPage: (val: number) => void;
  onFilterChanged?: (val: string) => void;
  onSortChanged?: (col: number) => void;
  testConnection?: (id: any) => void;
  editApi?: (id: any) => void;
  deleteApi?: (id: any) => void;
  downloadHandler: (fileId: string, row: string[]) => Promise<void>;
  setCurrentPage: (page: number) => void;

  width: number;
  darkMode: boolean;
  className?: string;
  colorArray?: any[];
}

const pageRowsSelectorContent = [
  { value: 25, label: "25" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
];

const SortableTableUI = (props: SortableTableUIProps) => {
  return (
    <>
      {props.standaloneTable && (
        <div className="search-row">
          <DropdownSelect
            width={props.width}
            title="page-rows"
            all="Max Page Rows"
            data={pageRowsSelectorContent}
            preselected={pageRowsSelectorContent[0]}
            isPageSelector
            onChange={(item: any) => props.setMaxRowsPerPage(item.value)}
            darkMode={props.darkMode}
          />
          {props.onFilterChanged && (
            <SearchInputRow
              darkMode={props.darkMode}
              currentValue={props.currentFilterKey ?? ""}
              onValueChange={(val) => {
                if (props.onFilterChanged) {
                  props.onFilterChanged(val);
                }
              }}
            />
          )}
        </div>
      )}
      <div className="table-wrapper">
        <table
          className={
            "table-" +
            (props.darkMode ? "dark" : "light") +
            "" +
            (props.standaloneTable ? " standalone" : "") +
            (props.className ? " " + props.className : "")
          }
        >
          <thead className="table-header">
            <tr>
              {props.colorArray && props.colorArray.length > 0 && (
                <th style={{ opacity: 0 }}>C</th>
              )}
              {props.header.map((h, idx) => (
                <th
                  key={props.id + "_head_" + idx}
                  className={
                    props.currentSort?.column === idx
                      ? props.currentSort.order === SortOrder.Ascending
                        ? "ascending"
                        : "descending"
                      : ""
                  }
                  onClick={() => {
                    if (props.onSortChanged) props.onSortChanged(idx);
                  }}
                >
                  {h +
                    (props.currentSort?.column === idx
                      ? props.currentSort.order === SortOrder.Ascending
                        ? " \u25b2"
                        : " \u25bc"
                      : "")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.pageRows.map((r, idx_r) => (
              <tr key={props.id + "_row_" + idx_r} style={{ fontWeight: 400 }}>
                {props.colorArray && props.colorArray[idx_r] && (
                  <td style={{ textAlign: "center" }}>
                    <div
                      className="color-legend"
                      style={{ backgroundColor: props.colorArray[idx_r] }}
                    />
                  </td>
                )}
                {r
                  .filter(
                    (_, idx_c) =>
                      !props.isAuditTable ||
                      (props.isAuditTable &&
                        idx_c < r.length - 1 - (props.isDocumentTable ? 2 : 0))
                  )
                  .map((elem: any, idx_c) => {
                    let downloadPath = elem;
                    let weightronAction: WeightronAction | null = null;
                    let rowConnectionTesting = false;
                    if (props.isAuditTable)
                      downloadPath = props.isDocumentTable
                        ? r[r.length - 3]
                        : r[r.length - 1];
                    if (props.isWeightingTable && idx_c > 3) {
                      rowConnectionTesting = props.isTestingConnection === elem;
                      switch (idx_c) {
                        case 4:
                          weightronAction = {
                            action: props.testConnection,
                            icon: rowConnectionTesting ? faSpinner : faSignal,
                          };
                          break;
                        case 5:
                          weightronAction = {
                            action: props.editApi,
                            icon: faPenToSquare,
                          };
                          break;
                        case 6:
                          weightronAction = {
                            action: props.deleteApi,
                            icon: faTrash,
                          };
                      }
                    }
                    return (
                      <td
                        key={props.id + "_col_" + idx_c}
                        style={{
                          textAlign:
                            !props.standaloneTable && idx_c !== 0
                              ? "end"
                              : "start",
                        }}
                      >
                        {props.isWeightingTable && idx_c > 3 ? (
                          <div
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              if (weightronAction?.action)
                                weightronAction.action(elem);
                            }}
                          >
                            {weightronAction && (
                              <FontAwesomeIcon
                                icon={weightronAction?.icon}
                                className="icon grey"
                                style={{
                                  color:
                                    idx_c === 4
                                      ? "green"
                                      : idx_c === 6
                                      ? "red"
                                      : "inherit",
                                }}
                              />
                            )}
                          </div>
                        ) : (
                          <>
                            {props.isWeightingTable && idx_c === 2 ? (
                              <div
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  if (props.testConnection)
                                    props.testConnection(elem);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={elem ? faCheck : faXmark}
                                  className="icon grey"
                                  style={{
                                    color: elem ? "green" : "red",
                                  }}
                                />
                              </div>
                            ) : (
                              <>
                                {!props.isTippingUser &&
                                props.hasDownloadLinks &&
                                idx_c === r.length - 1 &&
                                elem.length > 0 ? (
                                  <div
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                      props.downloadHandler(downloadPath, r)
                                    }
                                  >
                                    <FontAwesomeIcon
                                      icon={faDownload}
                                      className="icon grey"
                                    />
                                  </div>
                                ) : (
                                  <>
                                    {!props.isTippingUser &&
                                    props.isAuditTable &&
                                    idx_c === 2 ? (
                                      <div
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                          props.downloadHandler(downloadPath, r)
                                        }
                                      >
                                        {elem}
                                      </div>
                                    ) : (
                                      <>{elem}</>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {props.standaloneTable && (
        <div className={"page-navigator" + (props.darkMode ? " dark" : "")}>
          <div
            className={
              "pagination-btn" + (props.currentPage === 1 ? " disabled" : "")
            }
            onClick={() => {
              if (props.currentPage !== 1) props.setCurrentPage(1);
            }}
          >
            First
          </div>
          <div
            className={
              "pagination-btn" + (props.currentPage === 1 ? " disabled" : "")
            }
            onClick={() => {
              if (props.currentPage !== 1)
                props.setCurrentPage(props.currentPage - 1);
            }}
          >
            Prev
          </div>
          <TableLabels
            totalPages={props.totalPages}
            currentPage={props.currentPage}
            setCurrentPage={props.setCurrentPage}
          />
          <div
            className={
              "pagination-btn" +
              (props.currentPage === props.totalPages ? " disabled" : "")
            }
            onClick={() => {
              if (props.currentPage !== props.totalPages)
                props.setCurrentPage(props.currentPage + 1);
            }}
          >
            Next
          </div>
          <div
            className={
              "pagination-btn" +
              (props.currentPage === props.totalPages ? " disabled" : "")
            }
            onClick={() => {
              if (props.currentPage !== props.totalPages)
                props.setCurrentPage(props.totalPages);
            }}
          >
            Last
          </div>
        </div>
      )}
    </>
  );
};

export default SortableTableUI;
